import { isEmpty } from 'ramda';
import { FC } from 'react';
import Icon from 'shared/Icon';
import { Box, Flex, SxStyleProp, Text } from 'theme-ui';
import { ProjectFileBase } from 'types/ProjectFiles';
import { useTranslator } from '@picsart/rc/services/localization';

type ProjectFilesMenuProps = {
  containerRef: { current: HTMLDivElement };
  setCurrentProject: (id: string, index: number) => void;
  list: ProjectFileBase[];
  current: string;
  isScenesCreated: boolean;
};
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

const ProjectPreviewStyle: SxStyleProp = {
  width: '64px',
  height: '64px',
  borderRadius: 'button'
};

const ProjectPreviewStyleEmpty: SxStyleProp = {
  ...ProjectPreviewStyle,
  boxShadow: 'default'
};

export const ProjectFilesMenu: FC<ProjectFilesMenuProps> = ({
  current,
  list,
  containerRef,
  isScenesCreated,
  setCurrentProject
}) => {
  const translator = useTranslator();
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        zIndex: 'popup',
        px: 6,
        py: 7,
        top: '54px',
        bg: '#fff',
        width: 376,
        borderRadius: 'modal',
        boxShadow: 'default'
      }}
      data-test="projects-menu"
    >
      <Text variant="body" sx={{ fontSize: 2, mb: 6, color: 'grey7' }}>
        {translator('These_are_your_three_most_recent_projects.')}
        <br />
        {translator('Starting_a_new_project_will_overwrite_the_oldest_one.')}
      </Text>
      {isEmpty(list) ? (
        <Flex
          sx={{
            justifyContent: 'center',
            flexDirection: 'column',
            mt: '42px',
            alignItems: 'center',
            '.no-result-found-ill': {
              svg: {
                width: '120px'
              }
            }
          }}
        >
          <Icon name="no-result-found-ill" className="no-result-found-ill" />
          <Text
            variant="body"
            sx={{
              fontSize: 3,
              color: 'grey7',
              fontWeight: 'bold',
              mb: '46px',
              mt: 3
            }}
          >
            {translator('No_drafts_yet')}
          </Text>
        </Flex>
      ) : (
        list.map((item, i) => (
          <ProjectFilesItem
            key={item.id}
            disabled={isScenesCreated}
            isCurrent={item.id === current}
            setCurrentProjectFile={setCurrentProject}
            index={i}
            {...item}
          />
        ))
      )}
    </Box>
  );
};

export const ProjectFilesItem: FC<
  ProjectFileBase & {
    isCurrent: boolean;
    index: number;
    disabled: boolean;
    setCurrentProjectFile: (id: string, index: number) => void;
  }
> = ({ src, date, name, id, isCurrent, setCurrentProjectFile, disabled, index }) => (
  <Flex
    sx={{
      mt: 2,
      flexDirection: 'row',
      alignItems: 'center',
      p: 3,
      borderRadius: 'button',
      bg: isCurrent ? 'body' : '#fff',
      cursor: disabled ? 'unset' : 'pointer',
      '&:hover': {
        bg: 'body'
      },
      opacity: disabled ? 0.5 : 1
    }}
    onClick={() => !isCurrent && !disabled && setCurrentProjectFile(id, index)}
  >
    {src ? (
      <img src={src} sx={ProjectPreviewStyle} alt={name} />
    ) : (
      <Box sx={ProjectPreviewStyleEmpty} />
    )}
    <Box sx={{ ml: 5 }}>
      <Text
        variant="body"
        sx={{
          fontSize: 2,
          fontWeight: 'bold',
          color: 'grey6',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '220px'
        }}
        data-test="projects-item-name"
      >
        {name}
      </Text>
      <Text
        variant="body"
        sx={{
          fontSize: 2,
          fontWeight: 'body',
          color: 'grey6'
        }}
        data-test="projects-item-date"
      >
        {formatDate(date)}
      </Text>
    </Box>
  </Flex>
);
